import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  TSortColumn,
  TSortDirection,
} from 'src/app/shared/classes/grid-page-info';
import {
  GQLArguments,
  GQLResponse,
  IGQLResponse,
  IGQLSingleResponse,
} from 'src/app/shared/interfaces/graphql.interface';
import {
  getBillingIdListQuery,
  getGroupEnrollmentLogQuery,
  getGroupEnrollmentQuery,
  getGroupEnrollmentsQuery,
  getGroupEnrollmentStatuses,
} from 'src/app/shared/interfaces/group-enrollment/group-enrollment.graphql';
import {
  GroupEnrollmentStatus,
  GroupEnrollmentStatuses2,
  IGQLGroupEnrollmentLogParams,
  IGQLGroupEnrollmentParams,
  IGroupEnrollment,
  IGroupEnrollmentBillingIdListItemGQL,
  IGroupEnrollmentLog,
  IGroupEnrollmentStatus,
} from 'src/app/shared/interfaces/group-enrollment/group-enrollment.interface';
import { DEFAULT_PAGE_SIZE } from 'src/app/shared/variables/variables';

import { HelperGQLService } from '../../helper.graphql.service';

@Injectable({
  providedIn: 'root',
})
export class GroupEnrollmentGQLService {
  constructor(
    private apollo: Apollo,
    private gqlService: HelperGQLService,
  ) {}

  getGroupEnrollment(seqNo: number): Observable<IGroupEnrollment> {
    return this.apollo
      .query<IGQLSingleResponse<IGroupEnrollment>>({
        query: getGroupEnrollmentQuery,
        fetchPolicy: 'no-cache',
        variables: {
          seqNo,
        },
      })
      .pipe(
        map((response) => {
          return response.data.groupEnrollment;
        }),
      );
  }

  getGroupEnrollments(
    groupEnrollments?: IGQLGroupEnrollmentParams,
    userGroupsIdList: string[] = [],
  ): Observable<GQLResponse<IGroupEnrollment>> {
    const { orderby, direction, filter, after } = groupEnrollments ?? {};
    const sortBy: TSortColumn | undefined = orderby;
    const sortDirection: TSortDirection | undefined = direction;

    const gqlArguments: GQLArguments = { first: DEFAULT_PAGE_SIZE };

    const or = (x) => {
      return x?.length > 1
        ? {
            or: x.map((x) => {
              return { eq: x };
            }),
          }
        : { eq: x ? x[0] : null };
    };

    if (filter) {
      const groupEnrollmentsFilters: Record<string, unknown> = {
        enrollmentStatus: or(filter.enrollmentStatus),
        id:
          userGroupsIdList.length > 0
            ? or(userGroupsIdList)
            : { eq: filter.id },
        ediId: { eq: filter.ediId },
        npi: { eq: filter.npi },
        taxId: { eq: filter.taxId },
        payer: { name: { contains: filter.payerName } },
        payerId: { eq: filter.payerId },
        caseNumber: { contains: filter.caseNumber },
        enrollmentType:
          typeof filter.enrollmentType === 'string'
            ? filter.enrollmentType
            : or(filter.enrollmentType),
        enrollmentOwner: { contains: filter.enrollmentOwner },
        ...this.dateSetUp({
          dateFrom: filter.dateFrom ?? filter.showLast?.startDate,
          dateTo: filter.dateTo ?? filter.showLast?.endDate,
          statuses: filter.enrollmentStatus,
        }),
      };

      gqlArguments.where = this.gqlService.cleanFilterObject(
        groupEnrollmentsFilters,
      );
    }

    if (sortBy && sortDirection) {
      gqlArguments.order =
        typeof sortBy === 'object' ? [sortBy] : [{ [sortBy]: sortDirection }];
    }

    if (after) {
      gqlArguments.after = after;
    }

    return this.apollo
      .watchQuery<IGQLResponse<IGroupEnrollment>>({
        query: getGroupEnrollmentsQuery,
        fetchPolicy: 'no-cache',
        variables: {
          ...gqlArguments,
        },
      })
      .valueChanges.pipe(
        map((response) => {
          return response.data.groupEnrollments;
        }),
      );
  }

  getGroupEnrollmentStatuses(): Observable<IGroupEnrollmentStatus[]> {
    return this.apollo
      .query<IGQLSingleResponse<IGroupEnrollmentStatus[]>>({
        query: getGroupEnrollmentStatuses,
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((response) => {
          return response.data.enrollmentStatuses;
        }),
      );
  }

  getGroupEnrollmentLogs(
    groupEnrollmentsLogsParams?: IGQLGroupEnrollmentLogParams,
  ): Observable<GQLResponse<IGroupEnrollmentLog>> {
    const { filter, seqNo, after } = groupEnrollmentsLogsParams ?? {};

    const groupEnrollmentsFilters = filter
      ? {
          seqNo: { eq: filter.id },
          status: '',
          id: '',
          updated: '',
          userId: '',
        }
      : null;

    const where = groupEnrollmentsFilters
      ? this.gqlService.cleanFilterObject(groupEnrollmentsFilters)
      : null;

    const gqlArguments = this.gqlService.cleanFilterObject({
      first: DEFAULT_PAGE_SIZE,
      seqNo,
      after,
      where,
    });

    return this.apollo
      .query<IGQLResponse<IGroupEnrollmentLog>>({
        query: getGroupEnrollmentLogQuery,
        fetchPolicy: 'no-cache',
        variables: {
          ...gqlArguments,
        },
      })
      .pipe(
        map((response) => {
          return response.data.enrollmentLogs;
        }),
      );
  }

  getGroupEnrollmentNPIList(groupId: string): Observable<string[]> {
    return this.getGroupEnrollmentBillingIdListByTypeCode(groupId, 'XX');
  }

  getGroupEnrollmentTaxIdList(groupId: string): Observable<string[]> {
    return this.getGroupEnrollmentBillingIdListByTypeCode(groupId, 'EI');
  }

  private getGroupEnrollmentBillingIdListByTypeCode(
    groupId: string,
    billingIdTypeCode: string,
  ): Observable<string[]> {
    const gqlArguments = {
      where: {
        and: [
          { providerGroupId: { eq: groupId } },
          { billingIdTypeCode: { eq: billingIdTypeCode } },
          { status: { eq: 'A' } },
        ],
      },
      order: [{ billingId: 'ASC' }],
    };

    return this.apollo
      .query<IGQLResponse<IGroupEnrollmentBillingIdListItemGQL>>({
        query: getBillingIdListQuery,
        variables: { ...gqlArguments },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((response) => {
          return response.data.billingIds.nodes.map((x) => {
            return x.billingId;
          });
        }),
      );
  }

  private dateSetUp(params: dateParams) {
    const { dateFrom } = params;
    const { dateTo } = params;
    if (dateFrom && dateTo) {
      const { statuses = ['N'] } = params;

      type DataDetails = {
        and: Record<string, unknown>[];
      } & Record<string, unknown>;

      const data: DataDetails = {
        and: [],
      };

      for (const element of statuses) {
        const key = GroupEnrollmentStatuses2[element];
        if (key && key != 'M') {
          data.and.push(
            { [key]: { gte: dateFrom } },
            { [key]: { lte: dateTo } },
          );
        }
      }

      return this.gqlService.cleanFilterObject(data);
    } else {
      return null;
    }
  }
}

export type dateParams = {
  dateFrom?: string;
  dateTo?: string;
  statuses?: GroupEnrollmentStatus[];
};
